// src/components/PrivateRoute.tsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

interface JwtPayload {
  exp: number; // expiry timestamp (seconds)
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Public routes that don’t require authentication
const publicRoutes = [
  "/login",
  "/forgot-password",
  "/forgot-password/success",
  "/reset-password",
  "/reset-password/invalid",
  "/reset-password/expired",
];


interface CustomJwtPayload {
  exp?: number;
  userId?: string;
}


const isTokenValid = (authToken: string | null): boolean => {
  if (!authToken) return false;
  try {
    const decoded = jwtDecode<CustomJwtPayload>(authToken);
    if (!decoded?.exp) return false;

    // Protect if from guest portal
    if (!decoded.userId) return false;

    const now = Math.floor(Date.now() / 1000);
    return decoded.exp > now; // token still valid
  } catch (err) {
    console.error("Invalid token:", err);
    return false;
  }
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const authToken = localStorage.getItem("authToken");
  const valid = isTokenValid(authToken);
  const location = useLocation();

  // Allow access to public routes
  if (publicRoutes.includes(location.pathname)) {
    return <>{children}</>;
  }

  // Redirect unauthenticated users to login with redirect memory
  if (!valid) {
    return <Navigate to={`/login?redirect=${location.pathname}`} replace />;
  }

  return <>{children}</>;
};
